import React from "react";
import { Table } from "shared/core/Table";
import Button from "shared/components/button";
import { CellProps, Column } from "react-table";
import { useStaticData } from "context/staticData";
import { useCopies } from "shared/hooks/useCopies";
import { isBulkTicketAssignmentPending } from "../utils";
import BulkTicketStatusCell from "./bulkTicketStatusCell";
import { AssignedTicket, BulkTicketTableProps } from "../types";
import CellEllipsis from "shared/core/Table/components/cellEllipsis";
import { useAuth } from "shared/context/auth";

export default function BulkTicketTable(props: BulkTicketTableProps) {
  const copies = useCopies();
  const { userDetail } = useAuth();
  const staticDataObj = useStaticData();

  const getDisplayName = React.useCallback(
    ({ name, registrationParticipantId }: AssignedTicket) =>
      userDetail?.id === registrationParticipantId ? `${name} (${copies.get("me")})` : name,
    [copies, userDetail?.id]
  );

  const tableColumns: Array<Column<AssignedTicket>> = React.useMemo(() => {
    return [
      {
        id: "name",
        Header: copies.get("name"),
        accessor: getDisplayName,
        width: 100,
        Cell: CellEllipsis
      },
      {
        id: "workEmail",
        Header: copies.get("work.email"),
        accessor: "workEmail",
        width: 150,
        Cell: CellEllipsis
      },
      {
        id: "ticket-type",
        Header: copies.get("type"),
        accessor: "ticketFormName",
        width: 150,
        Cell: CellEllipsis
      },
      {
        id: "ticketStatus",
        Header: copies.get("status"),
        accessor: ({ ticketStatus }) => staticDataObj.ticketStatus?.[ticketStatus],
        width: 150,
        Cell: BulkTicketStatusCell
      },
      {
        id: "action",
        Header: copies.get("action"),
        accessor: ({ ticketStatus }) =>
          isBulkTicketAssignmentPending(ticketStatus) ? copies.get("cancel") : copies.get("revoke"),
        width: 80,
        Cell: (cellProps: CellProps<AssignedTicket>) => {
          const isTicketAssignmentPending = isBulkTicketAssignmentPending(cellProps.row.original.ticketStatus);
          const isLoggedInUser = userDetail?.id === cellProps.row.original.registrationParticipantId;

          return isLoggedInUser && !isTicketAssignmentPending ? (
            <span>{copies.get("empty.cell")}</span>
          ) : (
            <Button.Link
              label={
                isTicketAssignmentPending
                  ? copies.get("bulk.ticket.cancel.ticket")
                  : copies.get("bulk.ticket.revoke.ticket")
              }
              className="bulkTicket_action"
              onClick={() =>
                props.revokeTicketHandler(
                  cellProps.row.original.id,
                  isTicketAssignmentPending,
                  cellProps.row.original.uiVersion,
                  isLoggedInUser
                )
              }
            />
          );
        }
      }
    ];
  }, [copies, getDisplayName, props, staticDataObj.ticketStatus, userDetail?.id]);

  return (
    <Table<AssignedTicket>
      tableRef={props.tableRef}
      data={props.list}
      columns={tableColumns}
      disableFilters
      noRecordMessage={props.noRecordMessage}
      optionsHeight={0}
    />
  );
}
